import { useContext, useEffect, useRef } from 'react';
import { CircularProgress } from '@mui/material';
import { useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { ROUTES } from '../../../constants/routes';
import { usePermissions } from '../../../hooks/usePermissions';
import { PERMISSION } from '../../../shared/utils/permissions';
import {
  useAssignTicket,
  useReplyToTicket,
  useResolveTicket,
  useTicketQuery,
  useUpdateTicket,
} from '../../../hooks/useSupport';
import type { TicketCategory, TicketPriority } from '../../../types/support';
import { ErrorMessage } from '../../../components/common/ErrorMessage/ErrorMessage';
import { TicketDetailHeader } from './TicketDetailHeader';
import { TicketReplyBox } from './TicketReplyBox';
import { TranscriptMessage } from './TranscriptMessage';
import { useSupportEvents } from '../../../hooks/useSupportEvents';
import { UserContext } from '../../../context/userContext';
import './chat.css';

/**
 * One conversation as a chat: header (subject, status, origin, category,
 * priority, escalation reason, token usage), the thread, and — for agents
 * holding SUPPORT_MANAGE — assign / resolve and the reply composer. A SUPPORT_VIEW-only agent sees the transcript
 * read-only.
 */
export const TicketDetail = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const { has } = usePermissions();
  const canManage = has(PERMISSION.SUPPORT_MANAGE);
  const currentUserId = useContext(UserContext)?.user?.id;

  // Live conversation: a message the user just sent in Telegram lands in the
  // open transcript without the agent refreshing anything.
  useSupportEvents();
  const { data: ticket, isLoading, isError, refetch } = useTicketQuery(id ?? null);
  const assignMutation = useAssignTicket();
  const replyMutation = useReplyToTicket();
  const resolveMutation = useResolveTicket();
  const updateMutation = useUpdateTicket();

  const handleBack = () => navigate(`/${ROUTES.DASHBOARD}/${ROUTES.DASH_SUPPORT}`);

  const resolveReplyError = (): string | null => {
    if (!replyMutation.isError) {
      return null;
    }
    if (replyMutation.error instanceof Error) {
      return replyMutation.error.message;
    }
    return t('dashboard.support.replyError');
  };

    const isMine = Boolean(ticket?.assignedTo && ticket.assignedTo === currentUserId);
  const isClosed = ticket?.status === 'resolved' || ticket?.status === 'closed';

  // Follow the conversation: a new message (either side) scrolls into view.
  const threadRef = useRef<HTMLDivElement>(null);
  const messageCount = ticket?.messages.length ?? 0;
  useEffect(() => {
    const thread = threadRef.current;
    if (thread) thread.scrollTop = thread.scrollHeight;
  }, [messageCount]);

  /** One branch per load state; the chat is the last one standing. */
  const renderBody = () => {
    if (isError) {
      return (
        <ErrorMessage
          message={t('dashboard.support.errorLoadingTicket')}
          onRetry={() => void refetch()}
        />
      );
    }

    if (isLoading) {
      return (
        <div className="chat-empty" role="status">
          <CircularProgress size={28} />
        </div>
      );
    }

    if (!ticket) {
      return <ErrorMessage message={t('dashboard.support.notFound')} />;
    }

    const actions = canManage ? (
      <>
        {/* Already yours: offering to take it again is noise. */}
        {!isMine && (
          <button
            type="button"
            className="chat-action-btn"
            onClick={() => assignMutation.mutate(ticket._id)}
            disabled={assignMutation.isPending}
          >
            <span className="material-symbols-outlined" aria-hidden="true">person_add</span>
            {t('dashboard.support.assign')}
          </button>
        )}
        <button
          type="button"
          className="chat-action-btn is-primary"
          onClick={() => resolveMutation.mutate(ticket._id)}
          disabled={resolveMutation.isPending || isClosed}
        >
          <span className="material-symbols-outlined" aria-hidden="true">task_alt</span>
          {t('dashboard.support.resolve')}
        </button>
      </>
    ) : null;

    return (
      <>
        <TicketDetailHeader
          ticket={ticket}
          canManage={canManage}
          isUpdating={updateMutation.isPending}
          onBack={handleBack}
          actions={actions}
          onCategoryChange={(category: TicketCategory) =>
            updateMutation.mutate({ id: ticket._id, patch: { category } })
          }
          onPriorityChange={(priority: TicketPriority) =>
            updateMutation.mutate({ id: ticket._id, patch: { priority } })
          }
        />

        <div
          className="chat-thread"
          ref={threadRef}
          role="log"
          aria-label={t('dashboard.support.transcriptTitle')}
        >
          {ticket.messages.length === 0 ? (
            <p className="chat-empty">{t('dashboard.support.emptyThread')}</p>
          ) : (
            // The transcript has no stable per-message id; position is
            // stable for a given ticket's fetched history.
            ticket.messages.map((message, index) => (
              <TranscriptMessage key={index} message={message} />
            ))
          )}
        </div>

        {canManage ? (
          <TicketReplyBox
            onReply={(text) => replyMutation.mutate({ id: ticket._id, text })}
            isReplying={replyMutation.isPending}
            isReplySuccess={replyMutation.isSuccess}
            replyError={resolveReplyError()}
          />
        ) : (
          <p className="chat-readonly">{t('dashboard.support.readOnlyNotice')}</p>
        )}
      </>
    );
  };

  return (
    <div className="chat-page">
      <div className="chat-card">{renderBody()}</div>
    </div>
  );
};

export default TicketDetail;
